import type { EquipmentDto, EquipmentStatus, FinaleDto, PlantDto, PlantEquipmentDto, PlantSiteDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { APP_NAME } from '../../../lib/app-name';
import { readableAccent, useBrandData, useBrandFonts, useTenantLogo } from '../../../lib/brand';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { ErrorText } from '../../../ui';
import { DATA, FinaleScene, SUPPLY, mix, short, siteLayout, siteTheme, supplyOf, type SiteLayout } from './finale-scene';
import './finale.css';
import { RoomProposals, SiteSavings } from './PlantProposals';
import { RoleSheet } from './RoleWork';

/**
 * Il finale della configurazione guidata (ADR 0135): l'azienda come una pagina del suo sito, con il suo logo, i suoi
 * colori e i suoi caratteri. Si apre con la partita IVA da cui si è partiti, poi le sedi sulla mappa nella loro aura;
 * toccando una sede si entra nella sua pianta viva. Tutto viene dalla pianta (ADR 0034) e dai passi della
 * configurazione: gli stati degli impianti sono simulati finché l'azienda non è collegata, e la pagina lo dice.
 */
export function FinaleView() {
  const { tenant } = useTenant();
  const plant = useTenantQuery<PlantDto>(['plant'], '/onboarding/plant', { refetchInterval: (d) => (d?.generating ? 4000 : false) });
  const finale = useTenantQuery<FinaleDto>(['finale'], '/onboarding/finale', { refetchInterval: (d) => (d?.in_corso ? 3000 : false) });
  const brand = useBrandData(tenant.id).data;
  const logo = useTenantLogo(tenant.id);
  const fonts = useBrandFonts(tenant.id, brand);
  const [open, setOpen] = useState<string | null>(null);
  const top = useRef<HTMLDivElement>(null);

  const colori = useMemo(() => (brand?.colore ? [brand.colore, ...brand.colori.filter((c) => c !== brand.colore)] : []), [brand]);
  const accent = colori.length ? readableAccent(colori) : '#3b5bdb';
  const style = {
    '--fx-accent': accent,
    '--fx-paper': mix(accent, '#fffdf8', 0.95),
    '--fx-cream': mix(accent, '#fffefb', 0.975),
    '--fx-line': mix(accent, '#e8e4dc', 0.85),
    '--fx-ink': mix(accent, '#23262b', 0.88),
    '--fx-display': fonts.titoli ?? fonts.testo ?? 'Georgia, "Times New Roman", serif',
    '--fx-sans': fonts.testo ?? 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  } as CSSProperties;

  if (plant.error) return <ErrorText>{errorMessage(plant.error)}</ErrorText>;
  const p = plant.data;
  const f = finale.data;
  if (!p) return <div className="fx fx-loading" style={style} aria-busy />;

  const working = p.sedi.filter((s) => s.uso !== 'domicilio' && (s.reparti.length > 0 || s.impianti.length > 0));
  const quiet = p.sedi.filter((s) => !working.includes(s));
  const site = working.find((s) => s.id === open) ?? null;
  const name = f?.ragione_sociale ?? p.impresa;
  const host = brand?.sito ? brand.sito.replace(/^https?:\/\/(www\.)?/, '') : null;
  const go = (id: string | null) => {
    setOpen(id);
    top.current?.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  return (
    <div className="fx" style={style} ref={top}>
      <header className="fx-top">
        <div className="fx-brand">
          {logo ? <img src={logo} alt="" /> : <span className="fx-mono" aria-hidden>{name.slice(0, 1)}</span>}
          <div>
            <b>{name}</b>
            {host && <span>{host}</span>}
          </div>
        </div>
        <nav aria-label="Sezioni">
          <button type="button" onClick={() => go(null)} aria-current={!site ? 'page' : undefined}>
            Le sedi
          </button>
          {working.slice(0, 3).map((s) => (
            <button key={s.id} type="button" onClick={() => go(s.id)} aria-current={site?.id === s.id ? 'page' : undefined}>
              {short(s.nome, 22)}
            </button>
          ))}
        </nav>
        <div className="fx-by">
          <i />
          progettata da {APP_NAME}
        </div>
      </header>

      {site ? (
        <SiteView key={site.id} plant={p} site={site} index={working.indexOf(site)} colori={colori} onBack={() => go(null)} />
      ) : (
        <>
          <Hero plant={p} working={working} vat={f?.partita_iva ?? null} />
          <Sites plant={p} working={working} quiet={quiet} finale={f} colori={colori} onOpen={go} />
        </>
      )}

      <footer className="fx-foot">
        <div>
          <b>{name}</b>
          {f?.partita_iva && <> · P.IVA {f.partita_iva}</>}
          {(() => {
            const legal = p.sedi.find((s) => s.legale);
            return legal?.indirizzo || legal?.luogo ? <> · {[legal.indirizzo, legal.luogo].filter(Boolean).join(', ')}</> : null;
          })()}
        </div>
        <div>
          Pianta viva composta da {APP_NAME} con i dati della configurazione
          {p.generating && ' · si sta completando'}
        </div>
      </footer>
    </div>
  );
}

const NUM = ['zero', 'un', 'due', 'tre', 'quattro', 'cinque', 'sei', 'sette', 'otto', 'nove', 'dieci'];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const count = (n: number, one: string, many: string) => (n === 1 ? one : many);

function Hero({ plant, working, vat }: { plant: PlantDto; working: PlantSiteDto[]; vat: string | null }) {
  const reparti = working.reduce((a, s) => a + s.reparti.length, 0);
  const impianti = working.reduce((a, s) => a + s.impianti.length, 0);
  const names = working.map((s) => s.nome);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : (names[0] ?? plant.impresa);
  const stats: Array<[number, string]> = [
    [working.length, count(working.length, 'sede in cui si lavora', 'sedi in cui si lavora')],
    [reparti, count(reparti, 'reparto', 'reparti')],
    [impianti, count(impianti, 'impianto principale', 'impianti principali')],
    [plant.programmi.length, count(plant.programmi.length, 'programma che li tiene insieme', 'programmi che li tengono insieme')],
  ];
  return (
    <section className="fx-hero">
      <div className="fx-wrote">
        Ci hai scritto solo questo
        <span className="fx-vat">
          {vat ?? plant.impresa}
          <span className="fx-caret" aria-hidden />
        </span>
      </div>
      <h1>
        Al resto ha pensato <em>{APP_NAME}</em>.
      </h1>
      <p>
        {list}, con {working.length === 1 ? 'i suoi' : 'i loro'} reparti, gli impianti che {working.length === 1 ? 'la' : 'li'} fanno funzionare
        {plant.esterni.some((e) => e.categoria === 'consulenti') ? ', i professionisti che lavorano da fuori' : ''} e i programmi che tengono insieme ogni cosa. Ogni controllo ha la sua
        cadenza. Tocca una sede per entrarci.
      </p>
      <div className="fx-stats">
        {stats.map(([n, l]) => (
          <div key={l}>
            <b>{n}</b>
            <span>{l}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Distanza in linea d'aria, in chilometri. */
function km(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = (d: number) => (d * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lon - a.lon) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

const MAP = { w: 900, h: 470, pad: 120 };

function Sites({
  plant,
  working,
  quiet,
  finale,
  colori,
  onOpen,
}: {
  plant: PlantDto;
  working: PlantSiteDto[];
  quiet: PlantSiteDto[];
  finale: FinaleDto | undefined;
  colori: string[];
  onOpen: (id: string) => void;
}) {
  const all = [...working, ...quiet];
  const pos = finale?.posizioni ?? {};
  const known = all.filter((s) => pos[s.id]);
  // Proiezione semplice sulle sedi trovate: il riquadro si adatta, con un minimo per le sedi vicine.
  const meanLat = known.length ? known.reduce((a, s) => a + pos[s.id]!.lat, 0) / known.length : 42;
  const k = Math.cos((meanLat * Math.PI) / 180);
  const xs = known.map((s) => pos[s.id]!.lon * k);
  const ys = known.map((s) => pos[s.id]!.lat);
  const spanX = known.length ? Math.max(...xs) - Math.min(...xs) : 0;
  const spanY = known.length ? Math.max(...ys) - Math.min(...ys) : 0;
  // Almeno mezzo grado: due sedi nello stesso comune non finiscono una sull'altra al centro del mondo.
  const scale = Math.min((MAP.w - 2 * MAP.pad) / Math.max(0.5, spanX), (MAP.h - 2 * MAP.pad) / Math.max(0.3, spanY));
  const midX = known.length ? (Math.max(...xs) + Math.min(...xs)) / 2 : 0;
  const midY = known.length ? (Math.max(...ys) + Math.min(...ys)) / 2 : 0;
  const missing = all.filter((s) => !pos[s.id]);
  const xy = (s: PlantSiteDto): [number, number] => {
    const g = pos[s.id];
    if (g) return [MAP.w / 2 + (g.lon * k - midX) * scale, MAP.h / 2 - (g.lat - midY) * scale];
    const i = missing.indexOf(s);
    return [MAP.pad + ((i + 0.5) * (MAP.w - 2 * MAP.pad)) / missing.length, known.length ? MAP.h - 70 : MAP.h / 2];
  };
  const points = all.map((s) => ({ s, at: xy(s), big: working.includes(s) }));
  const cx = points.reduce((a, p) => a + p.at[0], 0) / Math.max(1, points.length);
  const cy = points.reduce((a, p) => a + p.at[1], 0) / Math.max(1, points.length);
  const rx = Math.min(430, Math.max(220, ...points.map((p) => Math.abs(p.at[0] - cx) + 120)));
  const ry = Math.min(225, Math.max(130, ...points.map((p) => Math.abs(p.at[1] - cy) + 90)));

  // Le due sedi più lontane, per la distanza in linea d'aria.
  let far: { a: PlantSiteDto; b: PlantSiteDto; d: number } | null = null;
  for (const a of known) for (const b of known) if (a !== b) {
    const d = km(pos[a.id]!, pos[b.id]!);
    if (!far || d > far.d) far = { a, b, d };
  }
  const where = (s: PlantSiteDto) => s.luogo?.replace(/\s+[A-Z]{2}$/, '') ?? s.nome;
  const n = all.length;
  const lead =
    far && far.d >= 5
      ? `Da ${where(far.a)} a ${where(far.b)}, circa ${Math.round(far.d / 10) * 10 || Math.round(far.d)} chilometri in linea d’aria. ${APP_NAME} le tiene tutte dentro la stessa aura.`
      : `${APP_NAME} tiene ${n === 1 ? 'la sede' : 'le sedi'} dentro la stessa aura, giorno e notte.`;

  return (
    <section className="fx-sedi" id="fx-sedi">
      <div className="fx-eyebrow">Le sedi</div>
      <h2>
        {n <= 10 ? cap(NUM[n]!) : n} {count(n, 'luogo', 'luoghi')}, una sola cura
      </h2>
      <p className="fx-lead">{lead}</p>
      <div className="fx-map">
        <svg viewBox={`0 0 ${MAP.w} ${MAP.h}`} role="img" aria-label={`Mappa delle sedi di ${plant.impresa}`}>
          <defs>
            <radialGradient id="fx-all">
              <stop offset="0%" stopColor="var(--fx-accent)" stopOpacity=".28" />
              <stop offset="60%" stopColor="var(--fx-accent)" stopOpacity=".09" />
              <stop offset="100%" stopColor="var(--fx-accent)" stopOpacity="0" />
            </radialGradient>
            {working.map((s, i) => (
              <radialGradient key={s.id} id={`fx-a-${i}`}>
                <stop offset="0%" stopColor={siteTheme(colori, i).base} stopOpacity=".5" />
                <stop offset="100%" stopColor={siteTheme(colori, i).base} stopOpacity="0" />
              </radialGradient>
            ))}
          </defs>
          <g className="fx-dots">
            {Array.from({ length: Math.ceil(MAP.w / 22) * Math.ceil(MAP.h / 22) }, (_, i) => (
              <circle key={i} cx={20 + (i % Math.ceil(MAP.w / 22)) * 22} cy={20 + Math.floor(i / Math.ceil(MAP.w / 22)) * 22} r="1" />
            ))}
          </g>
          <ellipse className="fx-aura" cx={cx} cy={cy} rx={rx} ry={ry} fill="url(#fx-all)" />
          <ellipse className="fx-ring" cx={cx} cy={cy} rx={rx * 0.9} ry={ry * 0.86} fill="none" stroke="var(--fx-accent)" strokeOpacity=".3" strokeDasharray="2 9" />
          {points.map((a, i) =>
            points.slice(i + 1).map((b) => (
              <path
                key={`${a.s.id}-${b.s.id}`}
                d={`M${a.at[0]} ${a.at[1]} Q ${(a.at[0] + b.at[0]) / 2} ${Math.min(a.at[1], b.at[1]) - 60} ${b.at[0]} ${b.at[1]}`}
                fill="none"
                stroke="var(--fx-accent)"
                strokeOpacity=".3"
                strokeDasharray="4 6"
              />
            )),
          )}
          {points.map(({ s, at: [x, y], big }) => {
            const i = working.indexOf(s);
            if (!big)
              return (
                <g key={s.id}>
                  <circle cx={x} cy={y} r="6" fill="var(--fx-muted)" stroke="#fff" strokeWidth="2" />
                  <text x={x} y={y + 24} textAnchor="middle" className="fx-map-sub">
                    {short(where(s), 24)} · {s.legale ? 'sede legale' : 'solo indirizzo'}
                  </text>
                </g>
              );
            const t = siteTheme(colori, i);
            return (
              <g
                key={s.id}
                className="fx-pin"
                tabIndex={0}
                role="button"
                aria-label={`Entra in ${s.nome}`}
                onClick={() => onOpen(s.id)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(s.id))}
              >
                <circle className="fx-aura" cx={x} cy={y} r="70" fill={`url(#fx-a-${i})`} />
                <circle cx={x} cy={y} r="13" fill={t.base} stroke="#fff" strokeWidth="3" />
                <circle className="fx-pulse" cx={x} cy={y} r="21" fill="none" stroke={t.base} strokeOpacity=".5" />
                <text x={x} y={y - 32} textAnchor="middle" className="fx-map-name">
                  {short(s.nome, 30)}
                </text>
                <text x={x} y={y + 40} textAnchor="middle" className="fx-map-sub">
                  {short(where(s), 30)}
                </text>
              </g>
            );
          })}
          <text x={MAP.w / 2} y={MAP.h - 18} textAnchor="middle" className="fx-map-foot">
            {finale?.in_corso && missing.length ? 'Sto mettendo le sedi al loro posto sulla mappa…' : `${APP_NAME} · ${n === 1 ? 'una sede' : `${n} sedi`} sotto la stessa cura`}
          </text>
        </svg>
      </div>
      <div className="fx-cards">
        {working.map((s, i) => {
          const t = siteTheme(colori, i);
          const progs = new Set(s.reparti.flatMap((r) => r.programmi)).size;
          return (
            <button key={s.id} type="button" className="fx-card" onClick={() => onOpen(s.id)}>
              <div className="ph" style={{ background: `linear-gradient(135deg, ${t.base}, ${t.night} 120%)` }} />
              <div className="bd">
                <div className="k">{s.attivita[0] ? short(s.attivita[0], 48) : s.legale ? 'Sede legale' : 'Sede'}</div>
                <h3>{s.nome}</h3>
                <div className="d">{[s.indirizzo, s.luogo].filter(Boolean).join(', ')}</div>
                <div className="d">
                  {s.reparti.length} {count(s.reparti.length, 'reparto', 'reparti')} · {s.impianti.length} {count(s.impianti.length, 'impianto', 'impianti')} · {progs} {count(progs, 'programma', 'programmi')}
                </div>
                <div className="go">Entra nella pianta viva →</div>
              </div>
            </button>
          );
        })}
        {quiet.map((s) => (
          <div key={s.id} className="fx-card quiet">
            <div className="bd">
              <div className="k">{s.legale ? 'Sede legale' : 'Solo indirizzo'}</div>
              <h3>{s.luogo ?? s.nome}</h3>
              <div className="d">{s.indirizzo}</div>
              <div className="d">Solo la sede dell’impresa: qui non ci sono reparti né impianti.</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Le scadenze e le cadenze di un reparto: gli obblighi dei suoi impianti, poi le sue attività che si ripetono. */
function watchOf(eqs: PlantEquipmentDto[], acts: Array<{ nome: string; cadenza: string | null }>): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const seen = new Set<string>();
  const push = (a: string, b: string) => {
    const k = a.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push([a, b]);
  };
  for (const e of eqs) for (const o of e.obblighi) push(o.descrizione, o.periodicita ?? 'da fissare');
  for (const a of acts) if (a.cadenza) push(a.nome, a.cadenza);
  return out.slice(0, 8);
}

function SiteView({ plant, site, index, colori, onBack }: { plant: PlantDto; site: PlantSiteDto; index: number; colori: string[]; onBack: () => void }) {
  const theme = siteTheme(colori, index);
  const lay = useMemo(() => siteLayout(site, plant), [site, plant]);
  const svg = useRef<SVGSVGElement>(null);
  const scene = useRef<FinaleScene | null>(null);
  const [sel, setSelected] = useState<string>('regia');
  const panel = useRef<HTMLElement>(null);
  // Con il pannello sotto la pianta, toccando un reparto il pannello si fa vedere.
  const setSel = (id: string) => {
    setSelected(id);
    const box = panel.current?.getBoundingClientRect();
    if (box && box.top > window.innerHeight - 120) panel.current!.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  const [off, setOff] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOff((o) => (o.has(id) ? new Set([...o].filter((x) => x !== id)) : new Set([...o, id])));

  useEffect(() => {
    if (!svg.current) return;
    const s = new FinaleScene(svg.current, lay, theme.glow, { onSelect: setSel, onToggle: toggle });
    scene.current = s;
    return () => s.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lay, theme.glow]);
  useEffect(() => scene.current?.setOff(off), [off, lay]);
  useEffect(() => scene.current?.select(sel), [sel, lay]);

  const shown = lay.rooms.flatMap((r) => r.eq.map((q) => q.e));
  const controls = site.impianti.reduce((a, e) => a + e.obblighi.length, 0);
  const offCount = shown.filter((e) => off.has(e.id)).length;
  const calm = offCount
    ? `${offCount === 1 ? 'Un impianto spento' : `${offCount} impianti spenti`}: ${APP_NAME} avvisa chi se ne occupa e tiene in ordine il resto`
    : `${APP_NAME} veglia su ${short(site.nome, 32)}: ${shown.length} ${count(shown.length, 'impianto', 'impianti')} in funzione${controls ? `, ${controls} ${count(controls, 'controllo', 'controlli')} in calendario` : ''}`;
  const kinds = [...new Set(shown.map((e) => e.porta))];
  const dataKinds = [...new Set(lay.data.map((d) => d.kind))];

  return (
    <section className="fx-scene">
      <div className="fx-crumbs">
        <button type="button" onClick={onBack}>
          ← Le sedi
        </button>
        <span>{site.attivita[0] ? short(site.attivita[0], 60) : site.luogo}</span>
      </div>
      <div className="fx-scene-head">
        <div>
          <h2>{site.nome}</h2>
          <p>{[site.indirizzo, site.luogo].filter(Boolean).join(', ')}</p>
        </div>
        {!!shown.length && (
          <div className="fx-tools">
            <button type="button" onClick={() => setOff(new Set())}>
              Accendi tutto
            </button>
            <button type="button" onClick={() => setOff(new Set(shown.map((e) => e.id)))}>
              Spegni tutto
            </button>
          </div>
        )}
      </div>
      <div className="fx-stage">
        <div className="fx-night" style={{ background: theme.night, ['--glow' as string]: theme.glow }}>
          <svg ref={svg} role="img" aria-label={`Pianta viva di ${site.nome}`} />
          <div className="fx-calm" aria-live="polite">
            <i />
            <span>{calm}</span>
          </div>
        </div>
        <aside className="fx-panel" aria-live="polite" ref={panel}>
          <Panel plant={plant} site={site} lay={lay} sel={sel} off={off} onToggle={toggle} onSelect={setSel} />
        </aside>
      </div>
      <div className="fx-legend">
        {kinds.map((k) => {
          const c = k ? SUPPLY[k] : { color: '#cfc8bb', label: 'Altro' };
          return (
            <span key={k ?? 'altro'}>
              <i style={{ background: c.color }} />
              {c.label}
            </span>
          );
        })}
        {dataKinds.map((k) => (
          <span key={k}>
            <i className="sq" style={{ background: DATA[k].color }} />
            {DATA[k].label}, dalla regia
          </span>
        ))}
      </div>
      <p className="fx-sim">
        Simulazione: gli stati degli impianti e i pacchetti che si muovono sono di esempio finché l’azienda non è collegata a {APP_NAME}. Tocca un impianto per spegnerlo o accenderlo, un
        reparto per vederne il dettaglio.
      </p>
    </section>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4>{title}</h4>
      {children}
    </div>
  );
}

function Panel({
  plant,
  site,
  lay,
  sel,
  off,
  onToggle,
  onSelect,
}: {
  plant: PlantDto;
  site: PlantSiteDto;
  lay: SiteLayout;
  sel: string;
  off: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  // Il ruolo toccato nel pannello del reparto: si apre la sua scheda (ADR 0137).
  const [role, setRole] = useState<string | null>(null);
  const room = lay.rooms.find((r) => r.id === sel);
  const roomName = (id: string) => lay.rooms.find((r) => r.id === id)?.nome ?? id;
  const extName = (id: string) => lay.ext.find((x) => x.id === id)?.nome ?? id;
  const watch = (rows: Array<[string, string]>) =>
    rows.length ? (
      <Block title={`${APP_NAME} tiene d’occhio`}>
        <div className="fx-watch">
          {rows.map(([a, b]) => (
            <div key={a}>
              <span>{a}</span>
              <b>{b}</b>
            </div>
          ))}
        </div>
      </Block>
    ) : null;

  if (sel === 'pro') {
    const pros = plant.esterni.map((e, i) => ({ e, id: `e:${i}` })).filter((x) => x.e.categoria === 'consulenti');
    return (
      <>
        <div className="k">Fuori dalla sede</div>
        <h3>Professionisti esterni</h3>
        <p>Chi lavora per l’azienda senza esserne dipendente. {APP_NAME} ricorda a ognuno cosa gli serve e quando.</p>
        <Block title="Chi sono e cosa fanno">
          <div className="fx-watch">
            {pros.map(({ e, id }) => {
              const with_ = [...new Set(site.flussi.filter((f) => f.da === id || f.a === id).map((f) => (f.da === id ? f.a : f.da)).filter((k) => k.startsWith('r:')).map(roomName))];
              return (
                <div key={id}>
                  <span>
                    {e.nome}
                    {!!with_.length && <small>con {with_.join(', ')}</small>}
                  </span>
                  <b className="wide">{e.cosa}</b>
                </div>
              );
            })}
          </div>
        </Block>
      </>
    );
  }

  if (!room || room.core) {
    const all = site.impianti.length;
    const on = site.impianti.filter((e) => !off.has(e.id)).length;
    const controls = site.impianti.reduce((a, e) => a + e.obblighi.length, 0);
    return (
      <>
        <div className="k">Il centro della sede</div>
        <h3>Regia {APP_NAME}</h3>
        <p>Qui arrivano i segnali dei reparti e degli impianti e da qui passano i dati verso enti e soggetti esterni. {APP_NAME} li ordina e avvisa chi deve fare cosa.</p>
        <SiteSavings site={site} />
        {!!plant.programmi.length && (
          <Block title="Programmi collegati">
            <div className="fx-chips">
              {plant.programmi.map((x) => (
                <span key={x}>{x}</span>
              ))}
            </div>
          </Block>
        )}
        {!!lay.data.length && (
          <Block title="Dati che passano di qui">
            <ul className="fx-rows">
              {lay.data.slice(0, 10).map((d, i) => (
                <li key={i}>
                  <button type="button" onClick={() => onSelect(d.ext === 'pro' ? 'pro' : d.room)}>
                    <i style={{ background: DATA[d.kind].color }} />
                    <span>
                      {d.out ? `${roomName(d.room)} → ${extName(d.ext)}` : `${extName(d.ext)} → ${roomName(d.room)}`}
                      <small>{d.cosa}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Block>
        )}
        {!!site.organi.length && (
          <Block title="Sopra la sede">
            <div className="fx-chips">
              {site.organi.map((o) => (
                <span key={o.nome} title={o.descrizione}>
                  {o.nome}
                </span>
              ))}
            </div>
          </Block>
        )}
        {watch([
          ...(all ? ([['Impianti in funzione', `${on} su ${all}`]] as Array<[string, string]>) : []),
          ...(controls ? ([['Controlli in calendario', String(controls)]] as Array<[string, string]>) : []),
          ['Reparti collegati', String(site.reparti.length)],
        ])}
      </>
    );
  }

  const rep = site.reparti.find((r) => `r:${r.id}` === room.id);
  const eqs = room.id === 'sede' ? site.impianti.filter((e) => !e.reparto) : site.impianti.filter((e) => `r:${e.reparto}` === room.id);
  return (
    <>
      <div className="k">{rep ? 'Reparto' : 'Tutta la sede'}</div>
      <h3>{room.nome}</h3>
      <p>{rep?.cosa ?? 'Gli impianti che servono tutta la sede, ognuno con i reparti a cui arriva.'}</p>
      {!!rep?.ruoli.length && (
        <Block title="Ruoli · tocca per procedure, mansioni e processi">
          <div className="fx-chips">
            {rep.ruoli.map((x) => (
              <button key={x.nome} type="button" className="fx-role" title={x.descrizione} onClick={() => setRole(x.nome)}>
                <span>
                  {x.nome}
                  {x.persone != null && <em> · {x.persone}</em>}
                </span>
              </button>
            ))}
          </div>
        </Block>
      )}
      {role && <RoleSheet nome={role} sede={site.nome} onClose={() => setRole(null)} />}
      {eqs.length ? (
        <Block title="Impianti · tocca per spegnere o accendere">
          <div className="fx-eq">
            {eqs.map((e) => (
              <EquipmentRow key={e.id} site={site} e={e} on={!off.has(e.id)} onToggle={() => onToggle(e.id)} to={e.serve.map((s) => (s === 'regia' ? `la regia` : roomName(`r:${s}`)))} />
            ))}
          </div>
        </Block>
      ) : (
        rep && <p className="fx-empty">Qui lavorano persone e programmi: nessun impianto da tenere acceso.</p>
      )}
      {!!rep?.programmi.length && (
        <Block title="Programmi in uso">
          <div className="fx-chips">
            {rep.programmi.map((x) => (
              <span key={x}>{x}</span>
            ))}
          </div>
        </Block>
      )}
      {watch(watchOf(eqs, rep?.attivita ?? []))}
      {rep && <RoomProposals siteId={site.id} roomId={rep.id} />}
    </>
  );
}

function EquipmentRow({ site, e, on, onToggle, to }: { site: PlantSiteDto; e: PlantEquipmentDto; on: boolean; onToggle: () => void; to: string[] }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const key = ['tenant', tenant.id, 'plant'];
  const answer = useMutation({
    mutationFn: (stato: EquipmentStatus) => api<EquipmentDto>(t(`/company/equipment/${e.id}`), { method: 'PATCH', body: { stato } }),
    // La pianta cambia subito: nessun caricamento dopo la risposta.
    onMutate: (stato) =>
      qc.setQueryData<PlantDto>(key, (old) =>
        old
          ? {
              ...old,
              sedi: old.sedi.map((s) =>
                s.id !== site.id ? s : { ...s, impianti: stato === 'non_presente' ? s.impianti.filter((x) => x.id !== e.id) : s.impianti.map((x) => (x.id === e.id ? { ...x, stato } : x)) },
              ),
            }
          : old,
      ),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  const c = supplyOf(e);
  return (
    <div className="fx-eq-row">
      <button type="button" className={on ? '' : 'off'} aria-pressed={on} onClick={onToggle}>
        <span className="dot" style={{ ['--c' as string]: c.color }} />
        <span>
          {e.nome}
          {!!to.length && <small>{c.label} verso {to.join(', ')}</small>}
        </span>
        <span className="st">
          {on ? 'in funzione' : 'spento'}
          {e.quantita != null && ` · ${e.quantita}`}
        </span>
      </button>
      {e.stato !== 'presente' && (
        <div className="fx-answer">
          <span>Da confermare:</span>
          {(
            [
              ['presente', 'C’è'],
              ['non_presente', 'Non c’è'],
              ['da_verificare', 'Non so'],
            ] as Array<[EquipmentStatus, string]>
          ).map(([s, l]) => (
            <button key={s} type="button" disabled={answer.isPending || e.stato === s} onClick={() => answer.mutate(s)}>
              {l}
            </button>
          ))}
          {answer.error && <ErrorText>{errorMessage(answer.error)}</ErrorText>}
        </div>
      )}
    </div>
  );
}
